<template>
  <div class="mt-4">
    <div
      v-if="!isMontlyView"
      class="flex w-full justify-between items-center bg-gray-100 p-1 py-4"
    >
      <span
        class="p-1 text-[1rem] rounded-md border cursor-pointer"
        :class="{ 'opacity-50 cursor-not-allowed': time <= 0 }"
        role="button"
        tabindex="2"
        @click="handleDateChange(-1)"
      >
        <i class="icon icon-angle-left-icon" />
      </span>

      <div
        class="align-middle text-xs"
        tabindex="1"
        role="button"
        @click="isDaily ? openDatePicker() : null"
      >
        <span class="hidden md:block text-base">{{ plannerLabel.desktop }}</span>
        <span class="block md:hidden text-sm">{{ plannerLabel.mobile }}</span>
      </div>

      <span
        class="p-1 rounded-md border cursor-pointer"
        :class="{ 'opacity-50 cursor-not-allowed': isMaxDateReached }"
        role="button"
        tabindex="1"
        @click="handleDateChange(1)"
      >
        <i class="icon icon-angle-right-icon" />
      </span>
    </div>
    <div
      v-else
      class="flex justify-center items-center bg-gray-100 p-1 py-4"
    >
      <select
        id="month-select"
        class="bg-transparent border-none m-0 text-center text-sm"
        @change="handleMonthsChange"
      >
        <option
          v-for="n in monthsToShow"
          :key="n"
          :selected="n === 1 ? 'selected' : undefined"
          :value="n"
        >
          Sur le{{ n !== 1 ? `s ${n}` : "" }} prochain{{ n !== 1 ? 's' : "" }} mois
        </option>
      </select>
    </div>
    <Modal
      v-model="isModalOpen"
      @close-dialog="handleCancel"
    >
      <template #body>
        <input
          type="date"
          :min="minDate"
          :max="maxDate"
          class="w-full px-4 py-2 text-gray-700 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          @change="handleDatePick"
          @click="$event.target.showPicker()"
        >
        <div class="flex justify-end mt-4">
          <button
            class="text-sm bg-gray-500 p-2 rounded-md text-white cursor-pointer"
            @click="handleCancel"
          >
            Fermer
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits } from 'vue';
import { add } from 'date-fns/add';
import { isBefore } from 'date-fns/isBefore';
import { differenceInDays } from 'date-fns/differenceInDays';
import { format } from 'date-fns/format';
import { startOfDay } from 'date-fns/startOfDay';
import { isAfter } from 'date-fns/isAfter';
import { fr } from 'date-fns/locale/fr';

import { frenchFormatedDate } from '@/utils/helpers.js';

import Modal from '../Modal.ce.vue';

const props = defineProps({
  timeframe: {
    type: String,
    default: ''
  },
  isDaily: {
    type: Boolean,
    default: true
  },
  timeLabel: {
    type: Object,
    default: () => ({})
  },
  monthsToShow: {
    type: Number,
    default: 1
  },
  time: {
    type: Number,
    default: 0
  }
});

const emit = defineEmits(['updateDate', 'dateChange', 'updateMonths']);

const isModalOpen = ref(false);

const isMontlyView = computed(() => {
  return props.timeframe === 'byMonths';
});

const isMaxDateReached = computed(() => {
  if (isMontlyView.value) {
    return;
  }

  const period = props.timeframe === 'daily' ? 'days' : 'weeks';

  const start = new Date(parseFloat(props.timeLabel.startDate));

  const maxDate = new Date(parseFloat(props.timeLabel.maxDate));

  return !isBefore(add(start, { [period]: props.time }), maxDate);
});

const minDate = computed(() => {
  return format(new Date(parseFloat(props.timeLabel.startDate)), 'yyyy-MM-dd');
});

const maxDate = computed(() => {
  return format(new Date(parseFloat(props.timeLabel.maxDate)), 'yyyy-MM-dd');
});

const openDatePicker = () => {
  isModalOpen.value = true;
};

const handleDateChange = (direction) => {
  const auxiliar = props.time + direction;
  if (auxiliar < 0) {
    return;
  }
  emit('dateChange', direction);
};

const handleCancel = () => {
  isModalOpen.value = false;
};

const handleMonthsChange = (event) => {
  emit('updateMonths', event.target.value);
};

const handleDatePick = (event) => {
  const date = new Date(event.target.value);
  const start = new Date(parseFloat(props.timeLabel.startDate));

  const days = differenceInDays(startOfDay(date), startOfDay(start));

  if (days > 0) {
    handleDateChange(days);
  }
  handleCancel();
};

const plannerLabel = computed(() => {
  if (!props.timeLabel) {
    return { mobile: '', desktop: '' };
  }

  switch (props.timeframe) {
    case 'daily': {
      const start = new Date(parseFloat(props.timeLabel.startDate));
      const startFormated = frenchFormatedDate(null, start, 'dd/MM/yyyy');
      return { mobile: `${startFormated}`, desktop: `${startFormated}` };
    }

    case 'weekly': {
      const start = new Date(parseFloat(props.timeLabel.startDate));
      const end = new Date(parseFloat(props.timeLabel.endDate));

      const mobileFirstDate = isAfter(startOfDay(start), startOfDay(new Date())) ? start : new Date();

      return { mobile: `${frenchFormatedDate(null, mobileFirstDate, 'dd/MM')} - ${frenchFormatedDate(null, end, 'dd/MM')}`, desktop: `Semaine du ${frenchFormatedDate(null, start, 'dd MMMM', { locale: fr })} au ${frenchFormatedDate(null, end, 'dd MMMM', { locale: fr })}` };
    }

    default:
  }

  return { mobile: '', desktop: '' };
});

</script>
